import { put, call, all, takeLatest, select } from 'redux-saga/effects';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import { getAllSavedItems } from 'yoda-interfaces/lib/SavedItems/SavedItemsApi';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import CurrencyFormatterLoader from 'yoda-site-components/lib/lazy/loaders/CurrencyFormatterLoader';
import { isGuestUser } from 'yoda-core-components/lib/helpers/Utils/Utils';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { DP_CHECKOUT_SAVED_ITEMS_PAYLOAD } from 'yoda-site-components/lib/common/Constants';
import {
    selectPreferences,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import * as ProductCartridgeActionTypes from 'yoda-site-components/lib/actionTypes/ProductCartridgeActionTypes';

const MAX_DISPLAY_NUM = 11;
const generateSlotData = (initialData, saveForLaterItems, totalItems, currencyFormatter) => {
    // const filteredItems = filter(saveForLaterItems, item => (item.itemId));
    // let productList = filteredItems;
    let items = null;
    let productItems = null;
    if (saveForLaterItems && saveForLaterItems.items) {
        items = saveForLaterItems.items;
        let showViewAllCard = false;
        if (totalItems > MAX_DISPLAY_NUM) {
            showViewAllCard = true;
        }

        const getItemType = (amounts) =>
            find(amounts, (item) => item.type && item.type.toLowerCase() === 'sale') ? 'SALE' : '';
        const getItemClearanceType = (amounts) =>
            find(amounts, (item) => item.type && item.type.toLowerCase() === 'clearance')
                ? 'CLEARANCE'
                : '';
        const getFormattedPrice = (price) =>
            currencyFormatter.format(price, { code: 'USD', precision: 2 });
        const getDiscount = (amounts) => {
            const amount = find(amounts, (item) => item.type && item.type.toLowerCase() === 'sale');
            return amount ? `${amount.minPercentOff}% off` : '';
        };
        const getSalePrice = (amounts) => {
            const saleAmount = find(
                amounts,
                (item) => item.type && item.type.toLowerCase() === 'sale'
            );
            return saleAmount ? getFormattedPrice(saleAmount.min) : '';
        };
        const getClearancePrice = (amounts) => {
            const clearanceAmount = find(
                amounts,
                (item) => item.type && item.type.toLowerCase() === 'clearance'
            );
            return clearanceAmount ? getFormattedPrice(clearanceAmount.min) : '';
        };
        const getDefaultPrice = (amounts) => {
            const defaultAmount = find(
                amounts,
                (item) => item.type && item.type.toLowerCase() === 'default'
            );
            return defaultAmount ? getFormattedPrice(defaultAmount.min) : '';
        };
        const getOriginalPrice = (amounts) => {
            const originalAmount = find(
                amounts,
                (item) => item.type && item.type.toLowerCase() === 'original'
            );
            return originalAmount ? getFormattedPrice(originalAmount.min) : '';
        };
        const mappedProductItems = map(items, (item) => ({
            linkURL: item.product.href,
            name: item.product.name,
            imageURL: item.product.image ? item.product.image.url : '',
            defaults: item.pricing ? getDefaultPrice(item.pricing.amounts) : '',
            original: item.pricing ? getOriginalPrice(item.pricing.amounts) : '',
            sale: item.pricing ? getSalePrice(item.pricing.amounts) : '',
            clearance: item.pricing ? getClearancePrice(item.pricing.amounts) : '',
            originalDesc: 'was',
            id: item.id,
            type: item.pricing ? getItemType(item.pricing.amounts) : '',
            clearenceType: item.pricing ? getItemClearanceType(item.pricing.amounts) : '',
            percentOff: item.pricing ? getDiscount(item.pricing.amounts) : '',
            rating: item.reviews ? item.reviews.rating : '',
            reviewCount: item.reviews ? item.reviews.reviewCount : '',
            skuId: item.itemId,
            ppId: item.product.id,
            productType: item.product.type,
            inventoryStatus: item.inventory ? item.inventory.message : '',
            marketingLabel: (item.pricing && item.pricing.marketingLabel) || '',
            viewAllCard: false,
            attributes: item.attributes,
            status: item.status,
        }));

        productItems = filter(
            mappedProductItems,
            (item) => !isEmpty(item.name) && !isEmpty(item.imageURL)
        );

        if (showViewAllCard) {
            productItems.push({ viewAllCard: true });
        }
    }

    const itemNumber =
        saveForLaterItems && saveForLaterItems.totalItems ? saveForLaterItems.totalItems : 0;
    const saveForLaterSchema = {
        schema: 'saved_items',
        items: productItems,
        itemNumber,
    };

    return { [initialData.payload.slotId]: saveForLaterSchema };
};

function generateSavedItemHeaderValue() {
    let savedItems = [];
    if (TokenProvider.get(DP_CHECKOUT_SAVED_ITEMS_PAYLOAD)) {
        savedItems = JSON.parse(TokenProvider.get(DP_CHECKOUT_SAVED_ITEMS_PAYLOAD)).reverse();
    }

    return reduce(
        savedItems,
        (result, item) => {
            let value = result;
            if (result.length > 0) {
                value = `${result},`;
            }
            return `${value}${item.product.id}:${item.itemId}`;
        },
        ''
    );
}

function* fetchDataFromSavedItems(initialData) {
    try {
        let verticalSflLimit = null;
        const preferences = yield select(selectPreferences);
        const isVerticalSflExpanded = yield select(
            (state) =>
                (state && state.sflActionStatus && state.sflActionStatus.isVerticalSflExpanded) ||
                false
        );

        verticalSflLimit = isVerticalSflExpanded
            ? preferences.maxItemsLimitInSFL
            : preferences.intialItemsInSFL;
        yield put({ type: ProductCartridgeActionTypes.ADD_LOADER_TO_LIST, initialData });
        const action = {
            payload: {
                params: {
                    limit:
                        (initialData.payload && initialData.payload.limit) ||
                        verticalSflLimit ||
                        MAX_DISPLAY_NUM,
                },
                cartSavedItems: true,
            },
        };

        let callApi = true;
        if (isGuestUser()) {
            action.payload.guestSavedItems = true;
            const itemData = yield call(generateSavedItemHeaderValue);
            action.payload.savedItemsData = itemData;
            if (!itemData || itemData.length === 0) {
                callApi = false;
            }
        }

        let result = {};
        let currencyFormatter;
        const { enablePreSourcing = true } = yield select(selectFeatureFlags) || {};
        if (enablePreSourcing) {
            action.payload.xClientSource = 'SAVE_FOR_LATER';
            action.payload.excludes = 'DELIVERY_DATES';
        }
        if (callApi) {
            action.payload.errorHandler = {
                context: 'CART',
                errorComponent: 'CART_SFL_SECTION',
            };
            [result, currencyFormatter] = yield all([
                call(FactorySaga, getAllSavedItems, action),
                CurrencyFormatterLoader(),
            ]);
        } else {
            result.isSuccess = true;
            result.response = {};
        }

        if (result.isSuccess) {
            const saveForLaterItems = result.response.data;
            const totalItems = saveForLaterItems ? saveForLaterItems.totalItems : 0;
            if (!currencyFormatter) {
                currencyFormatter = yield CurrencyFormatterLoader();
            }
            const slotData = yield call(
                generateSlotData,
                initialData,
                saveForLaterItems,
                totalItems,
                currencyFormatter
            );
            yield put({ type: ProductCartridgeActionTypes.ADD_PRODUCT_CARTRIDGE_SLOT, slotData });
        }
    } catch (error) {
        yield put({ type: ProductCartridgeActionTypes.FETCH_DATA_FROM_SAVED_ITEMS_ERROR, error });
    }
}

const watchSavedItemsProductListRequest = function* watchSavedItemsProductListRequest() {
    yield takeLatest(
        ProductCartridgeActionTypes.FETCH_DATA_SAVE_FOR_LATER,
        fetchDataFromSavedItems
    );
};

watchSavedItemsProductListRequest.sagaName = 'watchSavedItemsProductListRequest';

export default watchSavedItemsProductListRequest;
